import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useAppStore } from '@restorant/core';
import { useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { Sheet, SheetButton } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

/**
 * Change your own password, from anywhere, at any time.
 *
 * This replaces the screen that used to stand in front of the till at the first
 * sign-in after an owner issued a password. That screen was shown before anyone
 * had asked for it, in the middle of opening up, and the only way past it was
 * to decline — so it was declined, every time, and taught staff to tap through
 * whatever the app put in front of them.
 *
 * A sheet they open themselves is the same feature without the ambush, and it
 * keeps working long after the first day, which is when somebody actually
 * wants it.
 *
 * The old password is asked for because these devices stay signed in through a
 * shift on a counter anyone can reach. Without it, changing the owner's
 * password would be four taps for a passer-by.
 */
export function ChangePasswordSheet({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const changePassword = useAppStore((s) => s.changePassword);
  const showToast = useAppStore((s) => s.showToast);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A password left sitting in a field on a shared till is a password on
  // display, so the sheet is emptied every time it opens or closes.
  useEffect(() => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setBusy(false);
  }, [visible]);

  const submit = async () => {
    if (busy) return;
    setError(null);
    if (!current) return setError(t('current_password_required'));
    if (next.length < 6) return setError(t('password_too_short'));
    if (next !== confirm) return setError(t('passwords_differ'));
    if (next === current) return setError(t('password_unchanged'));

    setBusy(true);
    const err = await changePassword(current, next);
    setBusy(false);
    if (err) {
      // The store returns a sentinel rather than Supabase's own wording, whose
      // "Invalid login credentials" reads as "you have been signed out".
      return setError(err === 'CURRENT_PASSWORD_WRONG' ? t('current_password_wrong') : err);
    }
    showToast(t('password_changed_toast'));
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={t('change_password_title')}
      onClose={onClose}
      footer={
        <SheetButton
          label={busy ? t('signing_in') : t('set_password')}
          icon="check"
          tone="primary"
          onPress={() => void submit()}
        />
      }
    >
      <T size={12} color={colors.textFaint} style={{ lineHeight: 18 }}>
        {t('change_password_hint')}
      </T>

      <TextField
        testID="current-password"
        label={t('current_password')}
        icon="lock"
        value={current}
        onChangeText={setCurrent}
        secureTextEntry
      />
      <TextField
        testID="new-password"
        label={t('staff_password_new')}
        icon="lock"
        value={next}
        onChangeText={setNext}
        secureTextEntry
      />
      <TextField
        testID="new-password-confirm"
        label={t('confirm_password')}
        icon="lock"
        value={confirm}
        onChangeText={setConfirm}
        secureTextEntry
        onSubmitEditing={() => void submit()}
      />

      {error ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Icon name="close" size={14} color={colors.spice} />
          <T size={12} color={colors.spice} style={{ flexShrink: 1 }}>
            {error}
          </T>
        </View>
      ) : null}
    </Sheet>
  );
}
